import { EditCalendar, LocationOn, People, Person } from "@mui/icons-material";
import { Avatar, Box, Button, Card, CardContent, Grid, Toolbar, Typography } from "@mui/material";
import { useEffect, useState, useCallback, useMemo, memo, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Line } from "react-chartjs-2";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import useAuth from "../hooks/useAuth";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from "chart.js";
import socket from "../utils/socketConfig";
import { LazyAlertsWidget } from "../components/LazyComponents";
import MaintenanceWidget from "../components/MaintenanceWidget";
import { useTranslation } from "react-i18next";
import { useIntersectionObserver } from "../hooks/usePerformanceOptimizations";

// Registra gli elementi necessari
ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

// Componente card memoizzato per evitare re-render
const DashboardCard = memo(({ item }) => (
    <Grid item xs={6} md={2}>
        <Card sx={{ p: 2 }}>
            <Grid container spacing={1} alignItems="center">
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                        {item.title}
                    </Typography>
                </Grid>
                <Grid item xs={9}>
                    <Typography variant="h4" fontWeight="bold">
                        {item.value}
                    </Typography>
                </Grid>
                <Grid item xs={3}>
                    <Avatar
                        sx={{
                            width: { md: "50px", lg: "100px" },
                            height: { md: "50px", lg: "100px" },
                            fontSize: { md: "40px", lg: "70px" }
                        }}
                    >
                        {item.icon}
                    </Avatar>
                </Grid>
            </Grid>
        </Card>
    </Grid>
));

DashboardCard.displayName = 'DashboardCard';

// Componente sensore memoizzato
const SensorCard = memo(({ sensor, navigate, t }) => {
    const handleNavigate = useCallback(() => {
        navigate(`/sensors/${sensor.sensor_id}`);
    }, [navigate, sensor.sensor_id]);

    return (
        <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ p: 3 }}>
                <CardContent>
                    <Typography variant="h6">
                        {t('dashboard.widgets.sensors.title', "Sensore")} {sensor.sensor_id}
                    </Typography>
                    <Typography variant="body1">
                        {t('dashboard.widgets.weather.temperature', "Temperatura")}: {sensor.temperature}°C
                    </Typography>
                    <Typography variant="body1">
                        {t('dashboard.widgets.weather.humidity', "Umidità")}: {sensor.humidity}%
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={handleNavigate}
                        sx={{ mt: 2 }}
                    >
                        {t('dashboard.widgets.sensors.viewChart', "Visualizza Grafico")}
                    </Button>
                </CardContent>
            </Card>
        </Grid>
    );
});

SensorCard.displayName = 'SensorCard';

// Componente chart ottimizzato con intersection observer
const OptimizedChart = memo(({ data, options, type = 'Bar', title }) => {
  const [chartRef, isVisible] = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '50px'
  });

  const ChartComponent = type === 'Bar' ? Bar : Line;

  return (
    <Grid item xs={12} sm={6}>
      <Card sx={{ p: 3 }}>
        <Typography variant="h4">{title}</Typography>
        <div ref={chartRef} style={{ minHeight: '300px' }}>
          {isVisible ? (
            <ChartComponent data={data} options={options} />
          ) : (
            <div style={{ 
              height: '300px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px'
            }}>
              <Typography color="textSecondary">
                Chart will load when visible
              </Typography>
            </div>
          )}
        </div>
      </Card>
    </Grid>
  );
});

OptimizedChart.displayName = 'OptimizedChart';

// Widget meteo ottimizzato
const WeatherWidget = memo(({ t, temp, weather, humidity, weatherIcon }) => (
  <Grid item xs={12} sm={6}>
    <Card sx={{ p: 3 }}>
      <Typography variant="h4">
        {t('dashboard.widgets.weather.title', "Meteo a Firenze")}
      </Typography>
      {temp && weather && weatherIcon ? (
        <CardContent>
          <Typography variant="h6">{weather}</Typography>
          <Typography variant="h5">
            {t('dashboard.widgets.weather.temperature', "Temperatura")}: {temp}°C
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {t('dashboard.widgets.weather.humidity', "Umidità")}: {humidity}%
          </Typography>
          <Box sx={{ mt: 2 }}>
            <img
              src={weatherIcon}
              alt={t('dashboard.widgets.weather.icon', "Icona Meteo")}
              loading="lazy"
            />
          </Box>
        </CardContent>
      ) : (
        <Typography>
          {t('dashboard.widgets.weather.loading', "Caricamento dati meteo...")}
        </Typography>
      )}
    </Card>
  </Grid>
));

WeatherWidget.displayName = 'WeatherWidget';

// Widget appuntamenti
const AppointmentsWidget = memo(({ appointments, navigate, t }) => {
    const getTypeLabel = (type) => {
        const types = {
            'visita_medica': t('appointments.types.medical', 'Visita Medica'),
            'visita_parenti': t('appointments.types.family', 'Visita Parenti'),
            'ricovero': t('appointments.types.hospitalization', 'Ricovero'),
            'vacanza': t('appointments.types.vacation', 'Vacanza'),
            'altro': t('appointments.types.other', 'Altro')
        };
        return types[type] || type;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' });
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        return timeString.substring(0, 5); // HH:MM
    };

    return (
        <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', minHeight: 400 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                        {t('dashboard.widgets.appointments.title', 'Prossimi Appuntamenti')}
                    </Typography>
                    <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => navigate('appointments')}
                        startIcon={<EditCalendar />}
                    >
                        {t('dashboard.widgets.appointments.viewAll', 'Vedi tutti')}
                    </Button>
                </Box>
                {appointments.length > 0 ? (
                    <Box>
                        {appointments.map((apt) => (
                            <Card 
                                key={apt.appointment_id} 
                                sx={{ 
                                    mb: 2, 
                                    p: 2, 
                                    backgroundColor: 'action.hover',
                                    '&:hover': { backgroundColor: 'action.selected', cursor: 'pointer' }
                                }}
                                onClick={() => navigate('appointments')}
                            >
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Box sx={{ minWidth: 70 }}>
                                        <Typography variant="body2" color="primary" fontWeight="bold">
                                            {formatDate(apt.appointment_date)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatTime(apt.start_time)} - {formatTime(apt.end_time)}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight="medium" noWrap>
                                            {apt.elderly_name} {apt.elderly_surname}
                                            {apt.n_room && ` (${apt.n_room})`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {getTypeLabel(apt.appointment_type)}
                                        </Typography>
                                        {apt.doctor_name && (
                                            <Typography variant="caption" display="block" color="text.secondary" noWrap>
                                                Dr. {apt.doctor_name} {apt.doctor_surname}
                                            </Typography>
                                        )}
                                        {apt.external_doctor_name && (
                                            <Typography variant="caption" display="block" color="text.secondary" noWrap>
                                                {apt.external_doctor_name} (Esterno)
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Card>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <EditCalendar sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            {t('dashboard.widgets.appointments.noAppointments', 'Nessun appuntamento nei prossimi 7 giorni')}
                        </Typography>
                        <Button 
                            variant="contained" 
                            size="small"
                            sx={{ mt: 2 }}
                            onClick={() => navigate('appointments')}
                        >
                            {t('dashboard.widgets.appointments.createNew', 'Crea Appuntamento')}
                        </Button>
                    </Box>
                )}
            </Card>
        </Grid>
    );
});

AppointmentsWidget.displayName = 'AppointmentsWidget';

// Widget Eventi
const EventsWidget = memo(({ events, navigate, t }) => {
    const getTypeLabel = (type) => {
        const types = {
            'compleanno': 'Compleanno 🎂',
            'ricorrenza': 'Ricorrenza 🎉',
            'festa': 'Festa 🎊',
            'attivita': 'Attività 🎨',
            'gita': 'Gita 🚌',
            'conferenza': 'Conferenza 🎤',
            'spettacolo': 'Spettacolo 🎭',
            'altro': 'Altro'
        };
        return types[type] || type;
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: '2-digit' });
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        return timeString.substring(0, 5); // HH:MM
    };

    return (
        <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, height: '100%', minHeight: 400 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                        Prossimi Eventi
                    </Typography>
                    <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => navigate('calendar')}
                        startIcon={<EditCalendar />}
                    >
                        Vedi tutti
                    </Button>
                </Box>
                {events.length > 0 ? (
                    <Box>
                        {events.map((evt) => (
                            <Card 
                                key={evt.event_id} 
                                sx={{ 
                                    mb: 2, 
                                    p: 2, 
                                    backgroundColor: 'action.hover',
                                    '&:hover': { backgroundColor: 'action.selected', cursor: 'pointer' }
                                }}
                                onClick={() => navigate('calendar')}
                            >
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Box sx={{ minWidth: 70 }}>
                                        <Typography variant="body2" color="primary" fontWeight="bold">
                                            {formatDate(evt.event_date)}
                                        </Typography>
                                        {evt.event_time && (
                                            <Typography variant="caption" color="text.secondary">
                                                {formatTime(evt.event_time)}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight="medium" noWrap>
                                            {evt.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {getTypeLabel(evt.event_type)}
                                        </Typography>
                                        {evt.location && (
                                            <Typography variant="caption" display="block" color="text.secondary" noWrap>
                                                📍 {evt.location}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Card>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <EditCalendar sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            Nessun evento nei prossimi 7 giorni
                        </Typography>
                        <Button 
                            variant="contained" 
                            size="small"
                            sx={{ mt: 2 }}
                            onClick={() => navigate('calendar')}
                        >
                            Crea Evento
                        </Button>
                    </Box>
                )}
            </Card>
        </Grid>
    );
});

EventsWidget.displayName = 'EventsWidget';

const DashboardPage = () => {
    const { t } = useTranslation("global");
    const navigate = useNavigate();
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth(); // Aggiungi questo per controllare se il token è disponibile
    
    // Stati semplificati per evitare errori API
    const [elderlyCount, setElderlyCount] = useState(0);
    const [nursesCount, setNursesCount] = useState(0);
    const [doctorsCount, setDoctorsCount] = useState(0);
    const [appointmentsCount, setAppointmentsCount] = useState(0);
    const [eventsCount, setEventsCount] = useState(0);
    const [maintenanceCount, setMaintenanceCount] = useState(0);
    const [elderlyLoading, setElderlyLoading] = useState(false);
    const [alertsLoading, setAlertsLoading] = useState(false);
    const [sensors, setSensors] = useState([]);
    const [sensorsLoading, setSensorsLoading] = useState(false);
    const [alerts, setAlerts] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [appointmentsLoading, setAppointmentsLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [temp, setTemp] = useState(null);
    const [weather, setWeather] = useState("");
    const [humidity, setHumidity] = useState(null);
    const [weatherIcon, setWeatherIcon] = useState("");
    
    // Caricamento alerts semplificato - solo se il token è disponibile
    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        
        const loadAlerts = async () => {
            try {
                setAlertsLoading(true);
                const alertsResponse = await axiosPrivate.get('/alerts');
                setAlerts(alertsResponse.data || []);
            } catch (error) {
                console.error('Errore caricamento alerts:', error);
                setAlerts([]);
            } finally {
                setAlertsLoading(false);
            }
        };
        
        loadAlerts();
    }, [axiosPrivate, auth?.accessToken]);
        
    // Caricamento sensori semplificato con fallback - solo se il token è disponibile
    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        
        const loadSensors = async () => {
            try {
                setSensorsLoading(true);
                const response = await axiosPrivate.get("/sensors/daily");
                const uniqueSensors = response.data.reduce((acc, record) => {
                    if (!acc.find((sensor) => sensor.sensor_id === record.sensor_id)) {
                        acc.push({
                            sensor_id: record.sensor_id,
                            temperature: record.temperature,
                            humidity: record.humidity,
                        });
                    }
                    return acc;
                }, []);
                setSensors(uniqueSensors);
            } catch (error) {
                console.error('Errore caricamento sensori:', error);
                // Fallback con dati di esempio
                setSensors([
                    { sensor_id: 1, temperature: 22, humidity: 45 },
                    { sensor_id: 2, temperature: 24, humidity: 50 }
                ]);
            } finally {
                setSensorsLoading(false);
            }
        };
        
        loadSensors();
    }, [axiosPrivate, auth?.accessToken]);
    
    // Caricamento appuntamenti imminenti - prossimi 7 giorni
    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        
        const loadAppointments = async () => {
            try {
                setAppointmentsLoading(true);
                const response = await axiosPrivate.get('/appointments');
                
                // Filtra e ordina solo gli appuntamenti futuri (prossimi 7 giorni)
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const nextWeek = new Date(today);
                nextWeek.setDate(nextWeek.getDate() + 7);
                
                const upcomingAppointments = response.data
                    .filter(apt => {
                        const aptDate = new Date(apt.appointment_date);
                        return aptDate >= today && aptDate <= nextWeek && apt.status !== 'completed' && apt.status !== 'cancelled';
                    })
                    .sort((a, b) => {
                        const dateA = new Date(a.appointment_date + ' ' + a.start_time);
                        const dateB = new Date(b.appointment_date + ' ' + b.start_time);
                        return dateA - dateB;
                    })
                    .slice(0, 5); // Mostra solo i primi 5
                
                // Conta tutti gli appuntamenti attivi (non completati/cancellati)
                const totalActiveAppointments = response.data.filter(apt => 
                    apt.status !== 'completed' && apt.status !== 'cancelled'
                ).length;
                
                setAppointments(upcomingAppointments);
                setAppointmentsCount(totalActiveAppointments);
            } catch (error) {
                console.error('Errore caricamento appuntamenti:', error);
                setAppointments([]);
            } finally {
                setAppointmentsLoading(false);
            }
        };
        
        loadAppointments();
    }, [axiosPrivate, auth?.accessToken]);
    
    // Caricamento eventi
    useEffect(() => {
        if (!auth?.accessToken) return;
        
        const loadEvents = async () => {
            try {
                const response = await axiosPrivate.get('/events');
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const nextWeek = new Date(today);
                nextWeek.setDate(nextWeek.getDate() + 7);
                
                // Filtra eventi futuri prossimi 7 giorni
                const upcomingEvents = response.data
                    .filter(evt => {
                        const evtDate = new Date(evt.event_date);
                        return evtDate >= today && evtDate <= nextWeek;
                    })
                    .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
                    .slice(0, 3); // Massimo 3
                
                setEvents(upcomingEvents);
                
                // Conta tutti gli eventi futuri
                const activeEvents = response.data.filter(evt => {
                    const evtDate = new Date(evt.event_date);
                    return evtDate >= today;
                }).length;
                setEventsCount(activeEvents);
            } catch (error) {
                console.error('Errore caricamento eventi:', error);
                setEventsCount(0);
                setEvents([]);
            }
        };
        
        loadEvents();
    }, [axiosPrivate, auth?.accessToken]);
    
    // Caricamento manutenzioni
    useEffect(() => {
        if (!auth?.accessToken) return;
        
        const loadMaintenance = async () => {
            try {
                const response = await axiosPrivate.get('/maintenance/requests');
                const activeMaintenance = response.data.filter(m => 
                    m.status !== 'completata' && m.status !== 'annullata'
                ).length;
                setMaintenanceCount(activeMaintenance);
            } catch (error) {
                console.error('Errore caricamento manutenzioni:', error);
                setMaintenanceCount(0);
            }
        };
        
        loadMaintenance();
    }, [axiosPrivate, auth?.accessToken]);
    
    // Caricamento dati semplificato con fallback - solo se il token è disponibile
    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        
        const loadData = async () => {
            try {
                setElderlyLoading(true);
                const [elderlyResponse, nursesResponse, doctorsResponse] = await Promise.all([
                    axiosPrivate.get('/elderly/count'),
                    axiosPrivate.get('/nurses'),
                    axiosPrivate.get('/doctors')
                ]);
                
                setElderlyCount(elderlyResponse.data.count || elderlyResponse.data.length || 0);
                setNursesCount(nursesResponse.data.length || 0);
                setDoctorsCount(doctorsResponse.data.length || 0);
            } catch (error) {
                console.error('Errore caricamento dati:', error);
                // Fallback con dati di esempio se il DB non è disponibile
                setElderlyCount(25);
                setNursesCount(8);
                setDoctorsCount(5);
            } finally {
                setElderlyLoading(false);
            }
        };
        
        loadData();
    }, [axiosPrivate, auth?.accessToken]);

    // Memorizza la funzione di gestione per evitare re-render
    const handleNewAlert = useCallback((newAlert) => {
        setAlerts(prevAlerts => {
            if (prevAlerts.some(a => a.id === newAlert.id)) {
                return prevAlerts;
            }
            return [newAlert, ...prevAlerts];
        });
    }, [t]);

    // Socket listener ottimizzato
    useEffect(() => {
        const setupSocketListeners = () => {
            if (socket && typeof socket.on === 'function') {
                socket.on('newAlert', handleNewAlert);
            } else {
                console.error(t('dashboard.errors.socketInit', "Socket.IO non inizializzato correttamente in DashboardPage"));
            }
        };
        
        const timer = setTimeout(setupSocketListeners, 100);
        
        return () => {
            clearTimeout(timer);
            if (socket && typeof socket.off === 'function') {
                socket.off('newAlert', handleNewAlert);
            }
        };
    }, [handleNewAlert, t]);

    // Meteo fetch con lazy loading
    useEffect(() => {
        const fetchWeatherData = async () => {
            try {
                const response = await axiosPrivate.get('/api/weather/current?city=Florence');
                setTemp(response.data.temp);
                setWeather(response.data.description);
                setHumidity(response.data.humidity);
                setWeatherIcon(`/api/weather/icon/${response.data.icon}`);
            } catch (error) {
                console.error(t('dashboard.errors.weatherFetch', 'Errore nel recupero dati meteo:'), error);
            }
        };

        fetchWeatherData();
    }, [t, axiosPrivate]);

    // Dati delle card memoizzati
    const cardItems = useMemo(() => [
        {
            value: elderlyLoading ? '...' : (elderlyCount || 0),
            title: t('dashboard.cards.elderly', "Numero Anziani"),
            icon: <Person fontSize="inherit" />
        },
        {
            value: elderlyLoading ? '...' : (nursesCount || 0),
            title: "Numero Infermieri",
            icon: <People fontSize="inherit" />
        },
        {
            value: elderlyLoading ? '...' : (doctorsCount || 0),
            title: "Numero Dottori",
            icon: <People fontSize="inherit" />
        },
        {
            value: appointmentsLoading ? '...' : (appointmentsCount || 0),
            title: t('dashboard.cards.appointments', "Appuntamenti"),
            icon: <EditCalendar fontSize="inherit" />
        },
        {
            value: appointmentsLoading ? '...' : (eventsCount || 0),
            title: "Eventi",
            icon: <EditCalendar fontSize="inherit" />
        },
        {
            value: appointmentsLoading ? '...' : (maintenanceCount || 0),
            title: "Manutenzioni",
            icon: <EditCalendar fontSize="inherit" />
        }
    ], [elderlyCount, nursesCount, doctorsCount, appointmentsCount, eventsCount, maintenanceCount, elderlyLoading, appointmentsLoading, t]);

    // Opzioni chart memoizzate
    const chartOptions = useMemo(() => ({
        plugins: {
            legend: { display: false }
        },
        maintainAspectRatio: true,
        responsive: true
    }), []);

    // Dati chart memoizzati
    const chartData = useMemo(() => ({
        labels: [
            t('dashboard.charts.labels.homeCare', "Assistenza Domiciliare"), 
            t('dashboard.charts.labels.memoryCare', "Assistenza Memoria"), 
            t('dashboard.charts.labels.specialiseCare', "Assistenza Specialistica"), 
            t('dashboard.charts.labels.nursingCare', "Assistenza Infermieristica")
        ],
        datasets: [
            {
                label: t('dashboard.charts.population', "Popolazione"),
                data: [65, 55, 130, 140, 150],
                backgroundColor: "#303F9F",
                borderColor: "#303F9F"
            }
        ]
    }), [t]);

    // Loading state semplificato - rimuovo il check del token dal loading generale
    const isLoading = (elderlyLoading || alertsLoading || sensorsLoading) && auth?.accessToken;

    // Se non abbiamo token, mostriamo dashboard basic
    if (!auth?.accessToken) {
        return (
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: "calc(100% - 10px)"
                }}
            >
                <Toolbar />
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                            {t('dashboard.title', "Dashboard")}
                        </Typography>
                        <Typography variant="body1">
                            {t('dashboard.loadingAuth', "Verifica autenticazione in corso...")}
                        </Typography>
                    </Grid>
                </Grid>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: "calc(100% - 10px)",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh'
                }}
            >
                <Typography variant="h6">
                    {t('dashboard.loading', "Caricamento dashboard...")}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            component="main"
            sx={{
                flexGrow: 1,
                p: 3,
                width: "calc(100% - 10px)"
            }}
        >
            <Toolbar />

            <Grid container spacing={2}>
                {/* Widget Alert con lazy loading */}
                <Grid item xs={12}>
                    <Typography variant="h4" gutterBottom>
                        {t('dashboard.widgets.activeAlerts', "Alert Attivi")}
                    </Typography>
                    <Suspense fallback={<Typography>Caricamento alerts...</Typography>}>
                        <LazyAlertsWidget alerts={alerts} setAlerts={setAlerts} />
                    </Suspense>
                </Grid>

                {/* Cards contatori in un'unica riga (6 colonne) */}
                {cardItems.map((item, index) => (
                    <DashboardCard key={index} item={item} />
                ))}

                {/* Widget 3 colonne: Appuntamenti, Eventi, Manutenzioni */}
                <AppointmentsWidget 
                    appointments={appointments}
                    navigate={navigate}
                    t={t}
                />

                <EventsWidget
                    events={events}
                    navigate={navigate}
                    t={t}
                />

                <Grid item xs={12} md={4}>
                    <MaintenanceWidget />
                </Grid>

                {/* Widget dinamici per i sensori con lazy loading */}
                {sensorsLoading ? (
                    <Grid item xs={12}>
                        <Typography>
                            {t('dashboard.widgets.sensors.loading', "Caricamento sensori...")}
                        </Typography>
                    </Grid>
                ) : (
                    (sensors || []).map((sensor) => (
                        <SensorCard 
                            key={sensor.sensor_id} 
                            sensor={sensor} 
                            navigate={navigate} 
                            t={t} 
                        />
                    ))
                )}

                {/* Widget meteo in fondo */}
                <WeatherWidget 
                    t={t}
                    temp={temp}
                    weather={weather}
                    humidity={humidity}
                    weatherIcon={weatherIcon}
                />
            </Grid>
        </Box>
    );
};

export default DashboardPage;